import React, { useState } from "react";
import { Box, Button, MenuItem, TextField, makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import {
  ANSWER_TYPE_LABELS,
  AnswerType,
  CUSTOM_ANSWER_TYPES,
  FLOW_LABELS,
  MIN_ANSWER_OPTIONS,
  QUESTION_TEXT_LIMIT,
  QuestionFlow,
  isNewQuestionValid,
  parseOptionLines,
} from "./questionBank";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const FLOWS: QuestionFlow[] = ["business", "personal"];

interface Props {
  busy: boolean;
  defaultFlow: QuestionFlow;
  onAdd: (
    text: string,
    flow: QuestionFlow,
    answerType: AnswerType,
    answerOptions: string[]
  ) => void;
}

const AddQuestionForm = ({ busy, defaultFlow, onAdd }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const [flow, setFlow] = useState<QuestionFlow>(defaultFlow);
  const [answerType, setAnswerType] = useState<AnswerType>("yes_no");
  const [optionLines, setOptionLines] = useState<string>("");

  const valid = isNewQuestionValid(text, answerType, optionLines);

  const openForm = () => {
    setText("");
    setFlow(defaultFlow);
    setAnswerType("yes_no");
    setOptionLines("");
    setOpen(true);
  };

  const submit = () => {
    if (busy || !valid) {
      return;
    }
    onAdd(text.trim(), flow, answerType, parseOptionLines(optionLines));
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        color="primary"
        data-test-id="open-add-question"
        disabled={busy}
        fullWidth
        onClick={openForm}
        size="small"
        startIcon={<AddIcon />}
        variant="outlined"
      >
        Add Question
      </Button>
    );
  }

  return (
    <Box>
      <TextField
        autoFocus
        className={classes.field}
        data-test-id="new-question-text"
        fullWidth
        inputProps={{ maxLength: QUESTION_TEXT_LIMIT }}
        label="Question"
        multiline
        onChange={(event) => setText(event.target.value)}
        size="small"
        value={text}
        variant="outlined"
      />
      <TextField
        className={classes.field}
        fullWidth
        label="Flow"
        onChange={(event) => setFlow(event.target.value as QuestionFlow)}
        select
        size="small"
        value={flow}
        variant="outlined"
      >
        {FLOWS.map((option) => (
          <MenuItem key={option} value={option}>
            {FLOW_LABELS[option]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        className={classes.field}
        data-test-id="new-question-answer-type"
        fullWidth
        label="Answer"
        onChange={(event) => setAnswerType(event.target.value as AnswerType)}
        select
        size="small"
        value={answerType}
        variant="outlined"
      >
        {CUSTOM_ANSWER_TYPES.map((option) => (
          <MenuItem key={option} value={option}>
            {ANSWER_TYPE_LABELS[option]}
          </MenuItem>
        ))}
      </TextField>
      {answerType === "multi_select" && (
        <TextField
          className={classes.field}
          data-test-id="new-question-options"
          fullWidth
          helperText={`One answer per line, at least ${MIN_ANSWER_OPTIONS}`}
          label="Answers"
          multiline
          onChange={(event) => setOptionLines(event.target.value)}
          rows={3}
          size="small"
          value={optionLines}
          variant="outlined"
        />
      )}
      <Box className={classes.actions}>
        <Button
          color="primary"
          data-test-id="submit-new-question"
          disabled={busy || !valid}
          onClick={submit}
          size="small"
          variant="contained"
        >
          Add
        </Button>
        <Button disabled={busy} onClick={() => setOpen(false)} size="small">
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default AddQuestionForm;
